'use client';

import { useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';

import { useAcceptFriendRequest } from '@/modules/players/v3/hooks/use-accept-friend-request';
import { useBlockUser } from '@/modules/players/v3/hooks/use-block-user';
import { useDeclineFriendRequest } from '@/modules/players/v3/hooks/use-decline-friend-request';
import { useNudgeFriendRequest } from '@/modules/players/v3/hooks/use-nudge-friend-request';
import { useRevokeFriendRequest } from '@/modules/players/v3/hooks/use-revoke-friend-request';
import { useSendFriendRequest } from '@/modules/players/v3/hooks/use-send-friend-request';
import { useUnblockUser } from '@/modules/players/v3/hooks/use-unblock-user';
import { useUnfriend } from '@/modules/players/v3/hooks/use-unfriend';
import type { Friend } from '@/types/friends.types';

export interface FriendActions {
  onBlock: (friend: Friend) => void;
  onUnfriend: (friend: Friend) => void;
  onUnblock: (friend: Friend) => void;
  onAcceptFriendRequest: (friend: Friend) => void;
  onDeclineFriendRequest: (friend: Friend) => void;
  onNudgeFriendRequest: (friend: Friend) => void;
  onRevokeFriendRequest: (friend: Friend) => void;
  onSendFriendRequest: (friend: Friend) => void;
}

export interface UseFriendActionsOptions {
  onBlockSuccess?: () => void;
  onUnfriendSuccess?: () => void;
}

export function useFriendActions(
  userUuid: string,
  options?: UseFriendActionsOptions
): FriendActions {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const { mutate: blockMutate } = useBlockUser();
  const { mutate: unfriendMutate } = useUnfriend();
  const { mutate: unblockMutate } = useUnblockUser();
  const { mutate: acceptMutate } = useAcceptFriendRequest();
  const { mutate: declineMutate } = useDeclineFriendRequest();
  const { mutate: nudgeMutate } = useNudgeFriendRequest();
  const { mutate: revokeMutate } = useRevokeFriendRequest();
  const { mutate: sendRequestMutate } = useSendFriendRequest();

  const onBlock = useCallback(
    (friend: Friend) => {
      blockMutate(
        { uuid: userUuid, blockedUuid: friend.uuid },
        {
          onSuccess: () => {
            toast.success('Player blocked');
            optionsRef.current?.onBlockSuccess?.();
          },
          onError: () => toast.error('Failed to block player. Please try again.'),
        }
      );
    },
    [userUuid, blockMutate]
  );

  const onUnfriend = useCallback(
    (friend: Friend) => {
      unfriendMutate(
        { uuid: userUuid, friendUuid: friend.uuid },
        {
          onSuccess: () => {
            toast.success('Friend removed');
            optionsRef.current?.onUnfriendSuccess?.();
          },
          onError: () => toast.error('Failed to remove friend. Please try again.'),
        }
      );
    },
    [userUuid, unfriendMutate]
  );

  const onAcceptFriendRequest = useCallback(
    (friend: Friend) => {
      acceptMutate(
        { uuid: userUuid, friendUuid: friend.uuid },
        {
          onSuccess: () => toast.success('Friend request accepted'),
          onError: () => toast.error('Failed to accept friend request. Please try again.'),
        }
      );
    },
    [userUuid, acceptMutate]
  );

  const onDeclineFriendRequest = useCallback(
    (friend: Friend) => {
      declineMutate(
        { uuid: userUuid, friendUuid: friend.uuid },
        {
          onSuccess: () => toast.success('Friend request declined'),
          onError: () => toast.error('Failed to decline friend request. Please try again.'),
        }
      );
    },
    [userUuid, declineMutate]
  );

  const onUnblock = useCallback(
    (friend: Friend) => {
      unblockMutate(
        { uuid: userUuid, blockedUuid: friend.uuid },
        {
          onSuccess: () => toast.success('Player unblocked'),
          onError: () => toast.error('Failed to unblock player. Please try again.'),
        }
      );
    },
    [userUuid, unblockMutate]
  );

  const onNudgeFriendRequest = useCallback(
    (friend: Friend) => {
      nudgeMutate(
        { uuid: userUuid, friendUuid: friend.uuid },
        {
          onSuccess: () => toast.success('Friend request nudge sent'),
          onError: () => toast.error('Failed to nudge friend request. Please try again.'),
        }
      );
    },
    [userUuid, nudgeMutate]
  );

  const onRevokeFriendRequest = useCallback(
    (friend: Friend) => {
      revokeMutate(
        { uuid: userUuid, friendUuid: friend.uuid },
        {
          onSuccess: () => toast.success('Friend request cancelled'),
          onError: () => toast.error('Failed to cancel friend request. Please try again.'),
        }
      );
    },
    [userUuid, revokeMutate]
  );

  const onSendFriendRequest = useCallback(
    (friend: Friend) => {
      sendRequestMutate(
        { uuid: userUuid, friendUuid: friend.uuid },
        {
          onSuccess: (alreadyConnected) =>
            toast.success(
              alreadyConnected
                ? 'You are already connected with this player'
                : 'Friend request sent'
            ),
          onError: () => toast.error('Failed to send friend request. Please try again.'),
        }
      );
    },
    [userUuid, sendRequestMutate]
  );

  return useMemo<FriendActions>(
    () => ({
      onBlock,
      onUnfriend,
      onUnblock,
      onAcceptFriendRequest,
      onDeclineFriendRequest,
      onNudgeFriendRequest,
      onRevokeFriendRequest,
      onSendFriendRequest,
    }),
    [
      onBlock,
      onUnfriend,
      onUnblock,
      onAcceptFriendRequest,
      onDeclineFriendRequest,
      onNudgeFriendRequest,
      onRevokeFriendRequest,
      onSendFriendRequest,
    ]
  );
}
